import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import FileUploadField from "@/components/FileUploadField";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import BulkUploadDialog from "@/components/BulkUploadDialog";

const BLANK = {
  name: "", contact_email: "", contact_phone: "", address: "", gstin: "", pan: "",
  category: "", notes: "",
  gst_certificate_url: "", pan_card_url: "", incorporation_certificate_url: "",
};

export default function OrgVendors() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = async () => { const { data } = await api.get("/org/vendors"); setRows(data); };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit(null); setForm(BLANK); setOpen(true); };
  const openEdit = (v) => { setEdit(v); setForm({ ...BLANK, ...v }); setOpen(true); };

  const submit = async () => {
    try {
      if (edit) { await api.patch(`/org/vendors/${edit.id}`, form); toast.success("Vendor updated"); }
      else { await api.post("/org/vendors", form); toast.success("Vendor added"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor ${v.name}?`)) return;
    try { await api.delete(`/org/vendors/${v.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="org-vendors-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Vendors</h1>
          <p className="text-muted-foreground">All vendors you do business with — contacts, GST, PAN, certificates.</p>
        </div>
        <Button onClick={openCreate} data-testid="new-vendor-btn"><Plus className="w-4 h-4 mr-1" /> New vendor</Button>
      </div>
      <div className="flex justify-end">
        <BulkUploadDialog
          title="Bulk upload vendors"
          templateUrl="/org/vendors/template"
          uploadUrl="/org/vendors/bulk"
          payloadKey="vendors"
          testId="org-vendor-bulk"
          onSuccess={load}
        />
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>GSTIN</TableHead><TableHead>PAN</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((v) => (
              <TableRow key={v.id} data-testid={`vendor-row-${v.id}`}>
                <TableCell className="font-medium">{v.name}</TableCell>
                <TableCell className="text-xs">{v.category || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{v.contact_email || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{v.contact_phone || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{v.gstin || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{v.pan || "—"}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`vendor-edit-${v.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(v)} data-testid={`vendor-delete-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No vendors yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? `Edit vendor — ${edit.name}` : "New vendor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
              <div className="space-y-2"><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Clay / Glaze / Pigments…" /></div>
              <div className="space-y-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="border-t border-border pt-3 space-y-3">
              <div className="label-overline">Certificates</div>
              <FileUploadField label="GST certificate" value={form.gst_certificate_url} onChange={(v) => setForm({ ...form, gst_certificate_url: v })} testid="vendor-gst-upload" />
              <FileUploadField label="PAN card" value={form.pan_card_url} onChange={(v) => setForm({ ...form, pan_card_url: v })} testid="vendor-pan-upload" />
              <FileUploadField label="Incorporation certificate" value={form.incorporation_certificate_url} onChange={(v) => setForm({ ...form, incorporation_certificate_url: v })} testid="vendor-incorp-upload" />
            </div>
            <Button onClick={submit} disabled={!form.name} data-testid="vendor-save">Save vendor</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
